import React, { useState } from 'react';
import { Collapse } from 'reactstrap';
import { ChevronRight, ChevronDown } from 'react-feather';
import { ButtonBuilder } from '../../../../../../../../GlobalComponents/Builders';
import useConfirmModal from '../../../../../../../../GlobalComponents/Builders/ConfirmModal';
import CleanupActionsModal from './CleanupActionsModal';

/**
 * ManageJobModal — tiered danger panel for a DMT job, mirroring the record-level
 * Danger Zone on the progress page. Three tiers by blast radius:
 *   1. Cleanup      — delete job data (resources, mappings), rebuildable
 *   2. Lifecycle    — promote / archive, mostly irreversible state changes
 *   3. Delete       — permanently remove the job
 *
 * Props:
 *   jobData, jobResources, jobResourceMappings — job context
 *   jobKeyMappingsLength, jobValueMappingsLength — live counts for the cleanup rows
 *   handleDMTAction   — (action, formData, callback, showNotification, resetForm) => void
 *   getEverything     — refetch all job data
 *   getJobData        — refetch job header
 *   onDeleteJob       — callback to run the archive + delete + navigate sequence
 */
const ManageJobModal = ({
    jobData, jobResources, jobResourceMappings,
    jobKeyMappingsLength, jobValueMappingsLength,
    handleDMTAction, getEverything, getJobData, onDeleteJob,
}) => {

    const { confirm, ConfirmModal } = useConfirmModal();
    const [cleanupOpen, setCleanupOpen] = useState(false);

    const resourceCount = Object.values(jobResources || {}).reduce(
        (sum, c) => sum + (Array.isArray(c.selected_resources) ? c.selected_resources.length : 0), 0);
    const mappingCount = jobResourceMappings?.current?.length || 0;
    const keyCount = jobKeyMappingsLength || 0;
    const valueCount = jobValueMappingsLength || 0;

    const cleanupRows = [
        { label: 'Resources', count: resourceCount, unit: 'resource', action: 'delete-resources' },
        { label: 'Resource Mappings', count: mappingCount, unit: 'mapping', action: 'delete-resource-mappings' },
        { label: 'Key Mappings', count: keyCount, unit: 'mapping', action: 'delete-key-mappings' },
        { label: 'Value Mappings', count: valueCount, unit: 'mapping', action: 'delete-value-mappings' },
    ];

    const handleCleanupDelete = async (row) => {
        const confirmed = await confirm(
            `Delete all ${row.count} ${row.label.toLowerCase()} for this job? This cannot be undone.`,
            { title: `Delete ${row.label}`, confirmLabel: 'Delete' });
        if (!confirmed) return;
        handleDMTAction(row.action, null, () => getEverything(), null, true);
    };

    const handlePromote = async () => {
        const confirmed = await confirm(
            'Promote this job to use production credentials? This erases all data from the test migration and cannot be undone.',
            { title: 'Promote Job', confirmLabel: 'Promote' });
        if (!confirmed) return;
        handleDMTAction('promote-job', null, () => getJobData(), { isSync: jobData.is_sync });
    };

    const handleArchive = async () => {
        const confirmed = await confirm(
            'Archive this job? You will no longer be able to edit it, though you can still view it. This cannot be undone.',
            { title: 'Archive Job', confirmLabel: 'Archive' });
        if (!confirmed) return;
        handleDMTAction('archive-job', null, () => { window.location.reload(true); });
    };

    const handleDeleteJob = async () => {
        const confirmed = await confirm(
            'Permanently delete this job and all its data? It will be archived, then soft-deleted (set inactive). This cannot be undone.',
            { title: 'Delete Job', confirmLabel: 'Delete', size: 'md' });
        if (!confirmed) return;
        onDeleteJob();
    };

    return (
        <div>
            <p style={{ marginBottom: 16 }}>Review and manage job data. Actions are grouped by impact — each is independent and irreversible.</p>

            {/* Tier 1 — Cleanup (collapsed by default) */}
            <div>
                <div className='d-flex align-items-center justify-content-between' style={{ cursor: 'pointer' }}
                    onClick={() => setCleanupOpen(o => !o)}>
                    <div>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#6c757d', display: 'block' }}>Cleanup</span>
                        <span style={{ fontSize: 11, color: '#6c757d', display: 'block' }}>Delete job setup data. The job itself stays; you can rebuild these by re-running setup.</span>
                    </div>
                    {cleanupOpen
                        ? <ChevronDown size={18} style={{ color: '#6c757d', flexShrink: 0 }} />
                        : <ChevronRight size={18} style={{ color: '#6c757d', flexShrink: 0 }} />}
                </div>

                <Collapse isOpen={cleanupOpen}>
                    <div style={{ display: 'grid', gap: 12, marginTop: 8 }}>
                        {cleanupRows.map(r => (
                            <div key={r.action} className='d-flex justify-content-between align-items-center dmt-manage-row'>
                                <div>
                                    <div style={{ fontWeight: 600, display: 'inline' }}>{r.label}</div>
                                    <span className='dmt-cleanup-hint'>
                                        {r.count > 0 ? `${r.count} ${r.unit}(s)` : `No ${r.label.toLowerCase()}`}
                                    </span>
                                </div>
                                <ButtonBuilder label='Delete' className='btn-danger btn-sm' color={null}
                                    disabled={r.count === 0}
                                    onClick={() => handleCleanupDelete(r)} />
                            </div>
                        ))}
                    </div>

                    <hr className='dmt-manage-divider' />
                    <h6 style={{ marginBottom: 12 }}>Granular Cleanup</h6>
                    {/* Mounted only when expanded so cleanup-info fetch is deferred */}
                    {cleanupOpen && (
                        <CleanupActionsModal
                            jobData={jobData}
                            jobResources={jobResources}
                            jobResourceMappings={jobResourceMappings}
                            handleDMTAction={handleDMTAction}
                            getEverything={getEverything}
                        />
                    )}
                </Collapse>
            </div>

            {/* Tier 2 — Lifecycle */}
            <hr className='dmt-manage-divider' style={{ borderWidth: 3 }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: '#dc3545', display: 'block' }}>Lifecycle</span>
            <span style={{ fontSize: 11, color: '#6c757d', display: 'block', marginBottom: 8 }}>Change the job&apos;s state. These cannot be undone.</span>
            <div style={{ display: 'grid', gap: 12 }}>
                <div className='d-flex justify-content-between align-items-center dmt-manage-row'>
                    <div>
                        <div style={{ fontWeight: 600, display: 'inline' }}>Promote Job</div>
                        <span className='dmt-cleanup-hint'>Switch to production credentials; erases test migration data.</span>
                    </div>
                    <ButtonBuilder label='Promote' className='btn-secondary btn-sm' color={null} onClick={handlePromote} />
                </div>
                <div className='d-flex justify-content-between align-items-center dmt-manage-row'>
                    <div>
                        <div style={{ fontWeight: 600, display: 'inline' }}>Archive Job</div>
                        <span className='dmt-cleanup-hint'>Lock edits; the job stays viewable. Archive once completed.</span>
                    </div>
                    <ButtonBuilder label='Archive' className='btn-secondary btn-sm' color={null} onClick={handleArchive} />
                </div>
            </div>

            {/* Tier 3 — Delete */}
            <hr className='dmt-manage-divider' style={{ borderWidth: 3 }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: '#dc3545', display: 'block' }}>Delete</span>
            <span style={{ fontSize: 11, color: '#6c757d', display: 'block', marginBottom: 8 }}>Permanently removes the job.</span>
            <div className='d-flex justify-content-between align-items-center dmt-manage-row dmt-manage-row--danger'>
                <div>
                    <span style={{ fontWeight: 600, color: '#dc3545' }}>Delete Job</span>
                    <span className='dmt-cleanup-hint'>Archives the job, then soft-deletes it (sets inactive).</span>
                </div>
                <ButtonBuilder label='Delete Job' className='btn-danger btn-sm' onClick={handleDeleteJob} />
            </div>

            <ConfirmModal />
        </div>
    );
};

export default ManageJobModal;
